import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";

gsap.registerPlugin(ScrollTrigger);

const ExploreSection = () => {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate cards on scroll
      cardsRef.current.forEach((card, index) => {
        gsap.fromTo(card,
          {
            y: 100,
            opacity: 0,
            scale: 0.8
          },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 80%",
              end: "bottom 20%",
              toggleActions: "play none none reverse"
            },
            delay: index * 0.1
          }
        );
      });

      // Animate title
      gsap.fromTo(".explore-title", 
        {
          y: 50,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".explore-title",
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        }
      );

      // Animate description
      gsap.fromTo(".explore-description", 
        {
          y: 30,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay: 0.3,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".explore-description",
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        }
      );

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const designs = [
    { 
      title: "Modern Kitchen", 
      img: "/assets/Images/5l.webp",
      category: "Contemporary",
      features: ["Smart Storage", "LED Lighting", "Quartz Countertops"]
    },
    { 
      title: "Rustic Charm", 
      img: "/assets/Images/6l.webp",
      category: "Traditional",
      features: ["Natural Wood", "Vintage Hardware", "Warm Tones"]
    },
    { 
      title: "Elegant Design", 
      img: "/assets/Images/7l.webp",
      category: "Luxury",
      features: ["Premium Finish", "Custom Cabinetry", "High-End Appliances"]
    },
    { 
      title: "Urban Minimal", 
      img: "/assets/Images/8l.webp",
      category: "Minimalist",
      features: ["Clean Lines", "Hidden Storage", "Monochrome Palette"]
    },
    { 
      title: "Contemporary Space", 
      img: "/assets/Images/9l.webp",
      category: "Modern",
      features: ["Open Layout", "Mixed Materials", "Innovative Design"]
    },
    { 
      title: "Classic Aesthetic", 
      img: "/assets/Images/10l.webp",
      category: "Classic",
      features: ["Timeless Design", "Ornate Details", "Rich Colors"]
    },
  ];

  const addToCardRefs = (el) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  return (
    <section
      ref={sectionRef}
      className="explore-section py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden"
      aria-labelledby="explore-title"
    >
      {/* Background Decorations */}
      <div className="absolute top-10 left-10 w-4 h-4 bg-orange-400 rounded-full opacity-60 animate-pulse"></div>
      <div className="absolute top-1/4 right-20 w-3 h-3 bg-amber-400 rounded-full opacity-40 animate-pulse delay-1000"></div>
      <div className="absolute bottom-1/3 left-20 w-2 h-2 bg-orange-300 rounded-full opacity-50 animate-pulse delay-500"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <header className="text-center mb-16">
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full mb-6"
          >
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-semibold uppercase tracking-wide">
              Featured Projects
            </span>
          </motion.div>
          
          <h1
            id="explore-title"
            className="explore-title text-5xl md:text-6xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-6"
          >
            Explore Our Kitchen Designs
          </h1>
          
          <motion.p 
            className="explore-description text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Discover stunning kitchen designs that blend functionality with aesthetic appeal. 
            From modern to classic, our curated projects showcase expert craftsmanship and innovative design concepts.
          </motion.p>

         
        </header>

        {/* Designs Grid */}
        <div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          role="list"
        >
          {designs.map((design, index) => (
            <motion.article
              key={index}
              ref={addToCardRefs}
              className="group relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden"
              whileHover={{ y: -8 }}
              role="listitem"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
            >
              {/* Image Container */}
              <div className="relative h-80 overflow-hidden">
                <img
                  src={design.img}
                  alt={`${design.title} - premium kitchen design`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                    {design.category}
                  </span>
                </div>

                {/* Hover Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <h3 className="text-2xl font-bold text-white mb-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    {design.title}
                  </h3>
                  
                  {/* Features List */}
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-100">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {design.features.map((feature, featureIndex) => (
                        <span
                          key={featureIndex}
                          className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-medium"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                    
                    {/* CTA Button */}
                    <motion.button 
                      className="w-full bg-white text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-300 flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      aria-label={`View ${design.title} project details`}
                    >
                      View Project
                      <svg 
                        className="w-4 h-4 group-hover:translate-x-1 transition-transform" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </motion.button>
                  </div>
                </div>
              </div>
              
              {/* Default Content */}
              <div className="p-6 group-hover:opacity-0 transition-opacity duration-300">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {design.title}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {design.category} Style
                </p>
                <button className="text-orange-600 font-semibold text-sm flex items-center gap-1 group/link">
                  Explore Design
                  <svg 
                    className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </motion.article>
          ))}
        </div>

      
      </div>
    </section>
  );
};

export default ExploreSection;